import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AdminAuthProvider, ActorAuthProvider, useAdminAuth, useActorAuth } from "@/lib/auth";
import { DialogProvider } from "@/lib/dialog";
import { SettingsProvider } from "@/lib/settings";
import { EventProvider, useEvents } from "@/lib/events";
import { PORTAL_ENABLED } from "@/lib/config";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import PublicApply from "@/pages/PublicApply";
import AuditionSelect from "@/pages/AuditionSelect";
import TabletSignIn from "@/pages/TabletSignIn";
import CharacterSheet from "@/pages/CharacterSheet";
import RolesPublic from "@/pages/RolesPublic";
import RoleOffer from "@/pages/RoleOffer";
import JudgeScoring from "@/pages/JudgeScoring";

import AdminLogin from "@/pages/AdminLogin";
import Dashboard from "@/pages/admin/Dashboard";
import Events from "@/pages/admin/Events";
import Applicants from "@/pages/admin/Applicants";
import Cast from "@/pages/admin/Cast";
import ApplicantDetail from "@/pages/admin/ApplicantDetail";
import Auditions from "@/pages/admin/Auditions";
import AuditionDay from "@/pages/admin/AuditionDay";
import CastSessions from "@/pages/admin/CastSessions";
import CastSessionDay from "@/pages/admin/CastSessionDay";
import Roles from "@/pages/admin/Roles";
import RoleMatch from "@/pages/admin/RoleMatch";
import RoleDetail from "@/pages/admin/RoleDetail";
import ShowDates from "@/pages/admin/ShowDates";
import Roster from "@/pages/admin/Roster";
import TalentPool from "@/pages/admin/TalentPool";
import Settings from "@/pages/admin/Settings";
import Templates from "@/pages/admin/Templates";
import Reports from "@/pages/admin/Reports";
import Payroll from "@/pages/admin/Payroll";
import Messages from "@/pages/admin/Messages";
import FormBuilder from "@/pages/admin/FormBuilder";
import Notifications from "@/pages/admin/Notifications";

import PortalLogin from "@/pages/portal/PortalLogin";
import SetPassword from "@/pages/portal/SetPassword";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalAvailability from "@/pages/portal/PortalAvailability";
import PortalDetails from "@/pages/portal/PortalDetails";
import PortalShowInfo from "@/pages/portal/PortalShowInfo";
import PortalContract from "@/pages/portal/PortalContract";
import PortalNotifications from "@/pages/portal/PortalNotifications";

const FullPageLoader = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <Loader2 className="animate-spin text-blood" size={32} />
  </div>
);

function AdminGuard() {
  const { user } = useAdminAuth();
  const { currentId } = useEvents();
  if (user === undefined) return <FullPageLoader />;
  if (!user) return <Navigate to="/admin/login" replace />;
  // Keying by event id forces all scoped admin pages to remount (refetch) on event switch
  return <div key={currentId || "no-event"} className="contents"><Outlet /></div>;
}

function ActorGuard() {
  const { user } = useActorAuth();
  if (user === undefined) return <FullPageLoader />;
  if (!user) return <Navigate to="/portal/login" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <SettingsProvider>
      <DialogProvider>
        <AdminAuthProvider>
          <ActorAuthProvider>
            <EventProvider>
              <BrowserRouter>
                <Toaster position="bottom-right" theme="dark" />
                <AppRoutes />
              </BrowserRouter>
            </EventProvider>
          </ActorAuthProvider>
        </AdminAuthProvider>
      </DialogProvider>
    </SettingsProvider>
  );
}

function AppRoutes() {
  const location = useLocation();
  return (
    <Routes location={location} key={location.pathname}>
      {/* Public */}
      <Route path="/" element={<PublicApply />} />
      <Route path="/apply" element={<PublicApply />} />
      <Route path="/apply/:slug" element={<PublicApply />} />
      <Route path="/audition-select/:token" element={<AuditionSelect />} />
      <Route path="/signin" element={<TabletSignIn />} />
      <Route path="/signin/:showDateId" element={<TabletSignIn />} />
      <Route path="/role-offer/:token" element={<RoleOffer />} />
      <Route path="/judge" element={<JudgeScoring />} />
      <Route path="/:slug/roles" element={<RolesPublic />} />
      <Route path="/:slug/character/:rid" element={<CharacterSheet />} />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route element={<AdminGuard />}>
        <Route path="/admin" element={<Dashboard />} />
        <Route path="/admin/events" element={<Events />} />
        <Route path="/admin/applicants" element={<Applicants />} />
        <Route path="/admin/applicants/:id" element={<ApplicantDetail />} />
        <Route path="/admin/cast" element={<Cast />} />
        <Route path="/admin/auditions" element={<Auditions />} />
        <Route path="/admin/auditions/:aid" element={<AuditionDay />} />
        <Route path="/admin/rehearsals" element={<CastSessions />} />
        <Route path="/admin/rehearsals/:sid" element={<CastSessionDay />} />
        <Route path="/admin/roles" element={<Roles />} />
        <Route path="/admin/role-match" element={<RoleMatch />} />
        <Route path="/admin/roles/:rid" element={<RoleDetail />} />
        <Route path="/admin/show-dates" element={<ShowDates />} />
        <Route path="/admin/roster" element={<Roster />} />
        <Route path="/admin/talent-pool" element={<TalentPool />} />
        <Route path="/admin/settings" element={<Settings />} />
        <Route path="/admin/templates" element={<Templates />} />
        <Route path="/admin/reports" element={<Reports />} />
        <Route path="/admin/payroll" element={<Payroll />} />
        <Route path="/admin/messages" element={<Messages />} />
        <Route path="/admin/notifications" element={<Notifications />} />
        <Route path="/admin/form-builder" element={<FormBuilder />} />
      </Route>

      {/* Portal (disabled for now via PORTAL_ENABLED flag) */}
      {PORTAL_ENABLED ? (
        <>
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/set-password" element={<SetPassword />} />
          <Route element={<ActorGuard />}>
            <Route path="/portal" element={<PortalDashboard />} />
            <Route path="/portal/availability" element={<PortalAvailability />} />
            <Route path="/portal/details" element={<PortalDetails />} />
            <Route path="/portal/show-info" element={<PortalShowInfo />} />
            <Route path="/portal/contract" element={<PortalContract />} />
            <Route path="/portal/notifications" element={<PortalNotifications />} />
          </Route>
        </>
      ) : (
        <Route path="/portal/*" element={<Navigate to="/" replace />} />
      )}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
